import type { CatalogProductProps } from './catalog-product.type'
import type { ProfileProps } from './profiles.type'

import { CreditOrderProps } from './credit-order.type'
import type { TransactionProps } from './finance-transaction.type'
import type { LogisticShipmentProps } from './logistic-shipment.type'
import type { OrderInvoiceProps, OrderPaymentProps } from './order-finance.type'
import { SupplyOrderProps } from './supplier-order.type'
import { UserProps } from './user.type'

export enum SaleOrderStatusEnum {
  'created' = 'created',
  'processing' = 'processing',
  'completed' = 'completed',
  'cancelled' = 'cancelled',
  'reviewed' = 'reviewed',
  'fraudulent' = 'fraudulent',
}

export enum SaleOrderItemPackageEnum {
  '25kg_bags' = '25kg_bags',
  'super_sacks' = 'super_sacks',
  'in_bulk' = 'in_bulk',
  'gaylord_boxes' = 'gaylord_boxes',
  'rolls' = 'rolls',
}

export interface SaleOrderListItemProps {
  uuid: string
  orderItemUuid: string
  nonCatalogProduct: Record<string, string>
  catalogProduct?: CatalogProductProps
  quantity: number
  price: number
  cost?: number
  traderCommission?: number
  package: keyof typeof SaleOrderItemPackageEnum

  orderId: string
}

export interface SaleOrderListProps {
  uuid: string
  reference: string
  orderReference: string
  buyer: ProfileProps
  keyAccountManager: string
  status: keyof typeof SaleOrderStatusEnum
  totalOrderPrice: string
  totalOrderCost: string
  totalFreightPrice: string
  items: SaleOrderListItemProps[]
  invoices: OrderInvoiceProps[]
  payments: OrderPaymentProps[]
  shipments: LogisticShipmentProps[]
  createdAt: string
}

export interface SaleOrderFinanceProps extends SaleOrderListProps {
  invoices: OrderInvoiceProps[]
  payments: OrderPaymentProps[]
  transactions: TransactionProps[]
  totalPaidValue: string
}

export interface SaleOrderFinanceWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: SaleOrderFinanceProps[]
}

export interface SaleOrderLogisticProps extends SaleOrderListProps {
  shipments: LogisticShipmentProps[]
}

export interface SaleOrderLogisticWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: SaleOrderLogisticProps[]
}

export interface SaleOrderItemNonCatalogProductProps {
  description: string
  link: string
}

export interface SaleOrderItemProps {
  uuid: string
  orderItemUuid: string
  catalogProduct: CatalogProductProps
  catalogProductUuid: string
  nonCatalogProduct: SaleOrderItemNonCatalogProductProps
  quantity: number
  missingQuantity: number
  price: string
  cost: number
  traderCommission?: number
  package: keyof typeof SaleOrderItemPackageEnum
  // eslint-disable-next-line no-use-before-define
  saleOrder?: SaleOrderProps
  order: string
  createdAt: string
}

export interface SaleOrderQuotationProps {
  uuid: string
  reference: string
  creatorUser: string
  createdAt: string
}

export interface ProfileLocalProps extends Omit<ProfileProps, 'customerLevel'> {
  customerLevel: string
}

export interface SaleOrderKeyAccountManagerProps {
  id: string
  name: string
  email?: string
  phone?: string
  isActive?: boolean
  allowEmail?: boolean
  allowWhatsapp?: boolean
}

export enum SaleOrderPaymentCalculatedStatusEnum {
  'paid' = 'paid',
  'partially_paid' = 'partially_paid',
  'cancelled' = 'cancelled',
  'invalid' = 'invalid',
  'expired' = 'expired',
  'processing' = 'processing',
  'pending_credit_payment' = 'pending_credit_payment',
  'pending_credit_approval' = 'pending_credit_approval',
}
export interface SaleOrderCancellationReasonProps {
  reason: string
  observation?: string
}

export interface SaleOrderProps {
  uuid: string
  reference: string
  orderReference: string
  orderUuid: string
  buyer: ProfileLocalProps
  buyerUuid: string
  cm3Margin: string
  grossMargin: string
  keyAccountManagerId: string
  keyAccountManager: SaleOrderKeyAccountManagerProps
  items: SaleOrderItemProps[]
  totalProductPrice: string
  totalProductCost: string
  totalFreightPrice: string
  totalFreightCost: string
  taxPrice: string
  totalOrderPrice: string
  totalPaidValue: string
  dollarExchangeRate: string
  totalOrderCost: string
  financingCost: string
  discount: string
  includeIva: boolean
  invoicesStatus?: string
  shipmentsStatus?: string
  status: keyof typeof SaleOrderStatusEnum
  statusHistory: Record<string, string>
  statusReason?: string
  quotation: SaleOrderQuotationProps
  invoices: OrderInvoiceProps[]
  payments: OrderPaymentProps[]
  shipments: LogisticShipmentProps[]
  expiresAt: string
  updatedAt: string
  createdAt: string
  purchaseOnCredit: boolean
  transactions: TransactionProps[]
  paymentsStatus: keyof typeof SaleOrderPaymentCalculatedStatusEnum
  cancellationReason?: SaleOrderCancellationReasonProps
  creditOrders: CreditOrderProps[]
  disableNotifications?: boolean
}

export interface SaleOrderWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: SaleOrderProps[]
}

export interface SaleOrderCreditWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: SaleOrderProps[]
}

export enum SaleOrderCommentTypesEnum {
  'proof_of_delivery' = 'sentry_of_process__ordershipment__proof_of_delivery',
  'orderinvoice_file' = 'sentry_of_process__orderinvoice__file',
  'total_paid_value' = 'sentry_of_process__saleorder__total_paid_value',
  'orderpayment_proof' = 'sentry_of_process__orderpayment__proof',
  'profile_tax_status' = 'sentry_of_process__profile__tax_status',
  'order_item_price' = 'sentry_of_process__saleorder__saleorderitem_price',
  'order_item_cost' = 'sentry_of_process__saleorder__saleorderitem_cost',
  'order_item_quantity' = 'sentry_of_process__saleorder__saleorderitem_quantity',
  'order_shipment_customs_cost' = 'sentry_of_process__saleorder__shipments__customs_cost',
  'order_shipment_first_mile_cost' = 'sentry_of_process__saleorder__shipments__first_mile_cost',
  'order_shipment_last_mile_cost' = 'sentry_of_process__saleorder__shipments__last_mile_cost',
  'order_shipment_extra_cost' = 'sentry_of_process__saleorder__shipments__extra_cost',
  'order_shipment_expected_delivery_at' = 'sentry_of_process__saleorder__shipments__expected_delivery_at',
  'default' = 'default',
}

export interface SaleOrderCommentsProps {
  type: SaleOrderCommentTypesEnum
  subject?: string
  comment: string
  user: UserProps
  createdAt: string
  updatedAt?: string
  uuid?: string
}

export interface SaleOrderCommentsWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: SaleOrderCommentsProps[]
}

export interface SaleOrderSupplyOrdersResponse {
  supplyOrders?: SupplyOrderProps[]
  totalWithoutIva?: number
  total?: number
  iva?: number
  dollarTotalWithoutIva?: number
  dollarTotal?: number
  dollarIva?: number
}

export enum UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum {
  credit_balance = 'credit_balance',
  money_refund = 'money_refund',
  keep_inventory = 'keep_inventory',
  cancel_all = 'cancel_all',
}

export interface UpdateSupplyOrderOnSaleOrderCancelSupplyOrders {
  uuid: string
  cancellationDescription: string
  action: keyof typeof UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum
}

export interface UpdateSupplyOrderOnSaleOrderCancelPayload {
  supplyOrders: UpdateSupplyOrderOnSaleOrderCancelSupplyOrders[]
}